<script>
import { useAlert } from 'dashboard/composables';

// Every image field in this module (cover, unit image/layout, feature and
// nearby icons, gallery images) is now a real upload — the API has no
// plain "paste a URL" option for any of them (docs/real-estate/be/
// 10-api-reference.md §A4: "Every image is uploaded as multipart field
// `file`"). This component is deliberately dumb: it only picks a File and
// emits it — the parent owns the store dispatch and error mapping (same
// division of responsibility every other form in this module already
// uses), so it stays reusable across very differently-shaped call sites
// (a single cover photo vs. one gallery "add" button vs. a small icon).
//
// Type/size are checked here too (same allowlist and 10MB cap the server
// enforces, matching organization/services/CreateModal.vue's pattern) so a
// bad file is rejected instantly instead of round-tripping to find out.
//
// SVG is accepted only where `allowSvg` is set (feature/nearby icons). The
// server checks each SVG for scripts and refuses SVG everywhere else, since
// those images can be sent over WhatsApp.
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const SVG_TYPE = 'image/svg+xml';
const MAX_SIZE_BYTES = 10 * 1024 * 1024;

export default {
  name: 'ImageUpload',
  props: {
    previewUrl: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    removable: {
      type: Boolean,
      default: true,
    },
    // Smaller square thumbnail for inline rows (feature/nearby icons,
    // gallery list rows) vs. the default larger preview (cover, unit image).
    compact: {
      type: Boolean,
      default: false,
    },
    // Show the whole image instead of cropping it square — for drawings
    // (floor plan layouts) where a crop hides rooms.
    contain: {
      type: Boolean,
      default: false,
    },
    allowSvg: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    allowedTypes() {
      return this.allowSvg ? [...ALLOWED_TYPES, SVG_TYPE] : ALLOWED_TYPES;
    },
    accept() {
      return this.allowedTypes.join(',');
    },
  },
  methods: {
    pick() {
      this.$refs.input.click();
    },
    handleChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.allowedTypes.includes(file.type)) {
          useAlert(
            this.$t(
              this.allowSvg
                ? 'REAL_ESTATE.ERRORS.UNSUPPORTED_ICON_TYPE'
                : 'REAL_ESTATE.ERRORS.UNSUPPORTED_IMAGE_TYPE'
            )
          );
        } else if (file.size > MAX_SIZE_BYTES) {
          useAlert(this.$t('REAL_ESTATE.ERRORS.FILE_TOO_LARGE'));
        } else {
          this.$emit('select', file);
        }
      }
      // Reset so choosing the same file twice in a row still fires change.
      event.target.value = '';
    },
  },
};
</script>

<template>
  <div class="flex items-center gap-3">
    <div
      class="relative shrink-0 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-400"
      :class="compact ? 'w-12 h-12' : 'w-20 h-20'"
    >
      <fluent-icon icon="image" :size="compact ? 16 : 24" />
      <img
        v-if="previewUrl"
        :src="previewUrl"
        alt=""
        class="absolute inset-0 w-full h-full"
        :class="contain ? 'object-contain bg-white' : 'object-cover'"
        @error="$event.target.remove()"
      />
    </div>
    <div class="flex items-center gap-2">
      <woot-button type="button" variant="smooth" size="small" @click="pick">
        {{ label || $t('REAL_ESTATE.IMAGE_UPLOAD.CHOOSE_FILE') }}
      </woot-button>
      <woot-button
        v-if="removable && previewUrl"
        type="button"
        variant="clear"
        size="small"
        color-scheme="alert"
        icon="delete"
        @click="$emit('remove')"
      />
    </div>
    <input
      ref="input"
      type="file"
      :accept="accept"
      class="hidden"
      @change="handleChange"
    />
  </div>
</template>
